import { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, Filter, X, ExternalLink } from 'lucide-react';
import pb from '../lib/pocketbase';

import { API_URL, ADMIN_KEY } from '../lib/config';
const PB_URL = 'https://api.simpleclair.fr';

interface Job {
  id: string;
  submission_id: string;
  type: string;
  description: string;
  details: any;
  status: string;
  priority: number;
  started_at: string;
  completed_at: string;
  error: string;
  created: string;
  updated: string;
  expand?: {
    submission_id?: {
      business_name?: string;
      contact_email?: string;
      site_url?: string;
      logo_url?: string;
      activity?: string;
      location?: string;
      phone?: string;
      gmb_status?: string;
      gmb_location_id?: string;
    };
  };
  business_name?: string;
}

const JOB_TYPE_CONFIG: Record<string, { icon: string; label: string }> = {
  rebuild: { icon: '🔄', label: 'Rebuild site' },
  addon_blog: { icon: '📝', label: 'Blog' },
  addon_form: { icon: '📋', label: 'Formulaire' },
  addon_gmb_create: { icon: '📍', label: 'Création GMB' },
  addon_gmb_optimize: { icon: '🔧', label: 'Optim GMB' },
  addon_logo: { icon: '🎨', label: 'Logo' },
  addon_domain: { icon: '🌐', label: 'Domaine' },
  rejection_fix: { icon: '❌→✅', label: 'Correction rejet' },
  visibility_action: { icon: '📈', label: 'Visibilité' },
  other: { icon: '⚙️', label: 'Autre' },
};

const STATUS_CONFIG: Record<string, { badge: string; color: string; bg: string; text: string }> = {
  queued: { badge: '🟡', color: 'border-yellow-300', bg: 'bg-yellow-50', text: 'text-yellow-700' },
  in_progress: { badge: '🔵', color: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700' },
  review: { badge: '🟣', color: 'border-purple-300', bg: 'bg-purple-50', text: 'text-purple-700' },
  done: { badge: '🟢', color: 'border-green-300', bg: 'bg-green-50', text: 'text-green-700' },
  failed: { badge: '🔴', color: 'border-red-300', bg: 'bg-red-50', text: 'text-red-700' },
  rejected: { badge: '🟠', color: 'border-orange-300', bg: 'bg-orange-50', text: 'text-orange-700' },
  cancelled: { badge: '⚪', color: 'border-gray-300', bg: 'bg-gray-50', text: 'text-gray-500' },
};

const STATUS_LABELS: Record<string, string> = {
  queued: 'En attente',
  in_progress: 'En cours',
  review: 'À valider',
  done: 'Terminé',
  failed: 'Échoué',
  rejected: 'Rejeté',
  cancelled: 'Annulé',
};

type FilterType = 'all' | 'queued' | 'in_progress' | 'review' | 'done' | 'failed';

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'à l\'instant';
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h${minutes % 60 > 0 ? `${(minutes % 60).toString().padStart(2, '0')}` : ''}`;
  const days = Math.floor(hours / 24);
  return `${days}j`;
}

function duration(startStr: string, endStr?: string): string {
  if (!startStr) return '';
  const start = new Date(startStr).getTime();
  const end = endStr ? new Date(endStr).getTime() : Date.now();
  const diff = end - start;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}min ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h${(minutes % 60).toString().padStart(2, '0')}`;
}

// ==========================================
// Rejection Modal (T12)
// ==========================================
function RejectModal({ onConfirm, onCancel }: {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl p-6 w-[90%] max-w-md shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Pourquoi rejeter ce job ?</h3>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Ex: Le rebuild n'a pas mis à jour les horaires..."
          rows={4}
          className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-vertical outline-none focus:border-purple-300"
          autoFocus
        />
        <div className="flex gap-2 mt-4 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-500 bg-gray-50 rounded-lg hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            onClick={() => { if (reason.trim()) onConfirm(reason.trim()); }}
            disabled={!reason.trim()}
            className={`px-4 py-2 text-sm text-white rounded-lg font-medium ${
              reason.trim() ? 'bg-red-500 hover:bg-red-600 cursor-pointer' : 'bg-red-300 cursor-not-allowed'
            }`}
          >
            Rejeter
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Job Detail Modal (T13)
// ==========================================
function BlogArticlesPreview({ submissionId, siteUrl }: { submissionId: string; siteUrl?: string }) {
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams({
      perPage: '50',
      sort: '-created',
      filter: `submission_id='${submissionId}'`,
    });
    fetch(`https://api.simpleclair.fr/api/collections/articles/records?${params}`)
      .then(r => r.json())
      .then(data => {
        setArticles(data.items || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [submissionId]);

  if (loading) return <div className="text-xs text-gray-400">Chargement des articles...</div>;
  if (articles.length === 0) return <div className="text-xs text-gray-400">Aucun article trouvé</div>;

  return (
    <div>
      <span className="text-xs text-gray-400 block mb-2">📝 Articles créés ({articles.length})</span>
      <div className="space-y-2">
        {articles.map((article: any) => (
          <div key={article.id} className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800">{article.title}</p>
                {article.excerpt && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{article.excerpt}</p>
                )}
              </div>
              {siteUrl && article.slug && (
                <a
                  href={`${siteUrl}/blog/${article.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:text-blue-700 shrink-0 mt-0.5"
                >
                  👁️ Voir
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// Composant : Logs d'un job (T9 — logs consultables)
// ==========================================
interface LogEntry {
  ts: string;
  level: string;
  msg: string;
}

function JobLogsViewer({ jobId }: { jobId: string }) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/admin/jobs/${jobId}/logs`, {
      headers: { 'Authorization': `Bearer ${ADMIN_KEY}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: any) => {
        setLogs(data.logs || []);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Chargement des logs...
      </div>
    );
  }

  if (error) {
    return <div className="text-xs text-red-500 py-2">Erreur: {error}</div>;
  }

  if (logs.length === 0) {
    return <div className="text-xs text-gray-400 py-2 italic">Aucun log disponible pour ce job</div>;
  }

  const levelColors: Record<string, string> = {
    info: 'text-gray-600',
    warn: 'text-amber-600',
    error: 'text-red-600',
  };

  const levelBadges: Record<string, string> = {
    info: 'bg-gray-100 text-gray-600',
    warn: 'bg-amber-50 text-amber-700',
    error: 'bg-red-50 text-red-600',
  };

  return (
    <div className="bg-gray-900 rounded-xl p-4 max-h-64 overflow-y-auto font-mono text-xs leading-relaxed">
      {logs.map((entry, i) => {
        const ts = entry.ts?.substring(11, 19) || '';
        const level = entry.level || 'info';
        const color = level === 'error' ? 'text-red-400' : level === 'warn' ? 'text-amber-400' : 'text-gray-300';
        const tsColor = 'text-gray-500';
        const levelLabel = level === 'error' ? '❌' : level === 'warn' ? '⚠️' : '•';
        return (
          <div key={i} className={`flex gap-2 py-0.5 ${color} hover:bg-gray-800/50 px-1 rounded`}>
            <span className={tsColor}>{ts}</span>
            <span className="flex-shrink-0 w-4 text-center">{levelLabel}</span>
            <span className="break-all">{entry.msg}</span>
          </div>
        );
      })}
    </div>
  );
}

function JobDetailModal({ job, onClose, onValidate, onReject }: {
  job: Job;
  onClose: () => void;
  onValidate: () => void;
  onReject: () => void;
}) {
  const [showLogs, setShowLogs] = useState(false);
  const typeConf = JOB_TYPE_CONFIG[job.type] || JOB_TYPE_CONFIG.other;
  const statusConf = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
  const clientName = job.business_name || job.expand?.submission_id?.business_name || job.submission_id;
  const siteUrl = job.expand?.submission_id?.site_url;
  const logoUrl = job.expand?.submission_id?.logo_url;
  const details = job.details || {};

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-[90%] max-w-lg max-h-[80vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{typeConf.icon}</span>
            <div>
              <h3 className="font-semibold text-gray-900">{clientName}</h3>
              <span className="text-xs text-gray-400">{typeConf.label}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Status */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border ${statusConf.bg} ${statusConf.text} ${statusConf.color}`}>
              {statusConf.badge} {STATUS_LABELS[job.status]}
            </span>
            {job.priority > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                job.priority >= 2 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {job.priority >= 2 ? '🔥 Urgente' : '⚡ Haute'}
              </span>
            )}
          </div>

          {/* Description */}
          {job.description && (
            <div>
              <span className="text-xs text-gray-400 block mb-1">Description</span>
              <p className="text-sm text-gray-700">{job.description}</p>
            </div>
          )}

          {/* Rebuild: changed fields */}
          {job.type === 'rebuild' && details.changed_fields && (
            <div>
              <span className="text-xs text-gray-400 block mb-1">Champs modifiés</span>
              <div className="flex flex-wrap gap-1.5">
                {(details.changed_fields as string[]).map((field: string) => (
                  <span key={field} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                    {field}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Rebuild trigger */}
          {job.type === 'rebuild' && details.trigger && (
            <div>
              <span className="text-xs text-gray-400 block mb-1">Déclencheur</span>
              <span className="text-sm text-gray-700">{details.trigger}</span>
            </div>
          )}

          {/* Logo: show generated image (clickable for full size) */}
          {job.type === 'addon_logo' && logoUrl && (
            <div>
              <span className="text-xs text-gray-400 block mb-2">Logo généré</span>
              <div className="flex items-center gap-3">
                <a href={logoUrl} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl border border-gray-200 p-3 flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all" style={{ width: 100, height: 100 }}>
                  <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                </a>
                <a href={logoUrl} target="_blank" rel="noopener noreferrer" className="bg-gray-800 rounded-xl p-3 flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all" style={{ width: 100, height: 100 }}>
                  <img src={logoUrl} alt="Logo fond sombre" className="max-w-full max-h-full object-contain" />
                </a>
              </div>
            </div>
          )}

          {/* Blog addon: show articles */}
          {job.type === 'addon_blog' && (
            <BlogArticlesPreview submissionId={job.submission_id} siteUrl={siteUrl} />
          )}

          {/* GMB addon: show business info */}
          {(job.type === 'addon_gmb_create' || job.type === 'addon_gmb_optimize') && (() => {
            const sub = job.expand?.submission_id;
            const gmbStatus = sub?.gmb_status || 'inconnu';
            const gmbStatusColors: Record<string, string> = {
              verified: 'bg-green-100 text-green-700',
              pending_verification: 'bg-yellow-100 text-yellow-700',
              creation_failed: 'bg-red-100 text-red-700',
              created: 'bg-blue-100 text-blue-700',
            };
            return (
              <div className="space-y-3">
                <span className="text-xs text-gray-400 block">📍 Fiche Google Business</span>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{sub?.business_name || clientName}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${gmbStatusColors[gmbStatus] || 'bg-gray-100 text-gray-600'}`}>
                      {gmbStatus.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {sub?.activity && (
                    <p className="text-sm text-gray-600">{sub.activity}</p>
                  )}
                  <div className="grid grid-cols-1 gap-1.5 text-sm">
                    {sub?.location && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span>📌</span> {sub.location}
                      </div>
                    )}
                    {sub?.phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span>📞</span> {sub.phone}
                      </div>
                    )}
                    {sub?.contact_email && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <span>✉️</span> {sub.contact_email}
                      </div>
                    )}
                  </div>
                  {sub?.gmb_location_id && (
                    <div className="text-xs text-gray-400 mt-2">
                      Location ID: <code className="bg-gray-100 px-1 rounded">{sub.gmb_location_id}</code>
                    </div>
                  )}
                </div>
                {job.error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <span className="text-xs text-red-600 font-medium block mb-1">Erreur de création</span>
                    <p className="text-sm text-red-700">{job.error}</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Addon form details */}
          {job.type === 'addon_form' && details && (
            <div>
              <span className="text-xs text-gray-400 block mb-1">Détails</span>
              <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 overflow-x-auto">
                {JSON.stringify(details, null, 2)}
              </pre>
            </div>
          )}

          {/* Rejection reason */}
          {details.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <span className="text-xs text-red-600 font-medium block mb-1">Raison du rejet précédent</span>
              <p className="text-sm text-red-700">{details.rejection_reason}</p>
            </div>
          )}

          {/* Error */}
          {job.error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <span className="text-xs text-red-600 font-medium block mb-1">Erreur</span>
              <p className="text-sm text-red-700">{job.error}</p>
            </div>
          )}

          {/* Times */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-xs text-gray-400 block">Créé</span>
              <span className="text-gray-600">{timeAgo(job.created)}</span>
            </div>
            {job.started_at && (
              <div>
                <span className="text-xs text-gray-400 block">Durée</span>
                <span className="text-gray-600">{duration(job.started_at, job.completed_at || undefined)}</span>
              </div>
            )}
          </div>

          {/* Site link */}
          {siteUrl && (
            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 rounded-xl text-sm font-medium hover:bg-blue-100 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Voir le site du client
            </a>
          )}

          {/* Logs du job (T9 — job queue admin) */}
          <div>
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span>📋</span>
              <span className="font-medium">{showLogs ? 'Masquer les logs' : 'Voir les logs du job'}</span>
              <span className="text-xs">{showLogs ? '▲' : '▼'}</span>
            </button>
            {showLogs && (
              <div className="mt-2">
                <JobLogsViewer jobId={job.id} />
              </div>
            )}
          </div>
        </div>

        {/* Footer: validation buttons (T12) */}
        {job.status === 'review' && (
          <div className="p-5 border-t border-gray-100 flex gap-3">
            <button
              onClick={onValidate}
              className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition-colors"
            >
              ✅ Valider
            </button>
            <button
              onClick={onReject}
              className="flex-1 px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm font-semibold hover:bg-red-100 transition-colors"
            >
              ❌ Rejeter
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// Main Pipeline Component (T11 — pagination + archivage)
// ==========================================
const PAGE_SIZE = 10;

export default function Pipeline() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [rejectingJob, setRejectingJob] = useState<Job | null>(null);

  const fetchJobs = useCallback(async (page = 1, append = false) => {
    if (append) setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (filter !== 'all') {
        params.set('status', filter);
      }
      if (includeArchived) {
        params.set('includeArchived', 'true');
      }
      const res = await fetch(`${API_URL}/admin/jobs?${params}`, {
        headers: { 'Authorization': `Bearer ${ADMIN_KEY}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as any;
      const newItems = data.items || [];
      if (append) {
        setJobs(prev => [...prev, ...newItems]);
      } else {
        setJobs(newItems);
      }
      setTotalItems(data.totalItems || 0);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.page || page);
      setError(null);
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter, includeArchived]);

  // Reset to page 1 when filter or archive toggle changes
  useEffect(() => {
    setJobs([]);
    setCurrentPage(1);
    setLoading(true);
    fetchJobs(1, false);
  }, [filter, includeArchived]);

  // Auto-refresh every 10s (page 1 only to keep it simple)
  useEffect(() => {
    const interval = setInterval(() => fetchJobs(1, false), 10000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      fetchJobs(currentPage + 1, true);
    }
  };

  const handleAction = async (jobId: string, action: 'cancel' | 'retry' | 'validate' | 'reject', rejectionReason?: string) => {
    try {
      const body: Record<string, any> = {};
      if (action === 'cancel') body.status = 'cancelled';
      if (action === 'retry') {
        body.status = 'queued';
        body.error = '';
        body.started_at = '';
        body.completed_at = '';
      }
      if (action === 'validate') body.status = 'done';
      if (action === 'reject') {
        body.status = 'rejected';
        body.rejection_reason = rejectionReason || '';
      }
      await fetch(`${API_URL}/admin/jobs/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${ADMIN_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      setSelectedJob(null);
      setRejectingJob(null);
      // Reload from page 1 after action
      setJobs([]);
      setCurrentPage(1);
      fetchJobs(1, false);
    } catch (e: any) {
      console.error('Action failed:', e);
    }
  };

  // Stats (computed from loaded jobs — best effort for visible items)
  const stats = {
    queued: jobs.filter(j => j.status === 'queued').length,
    in_progress: jobs.filter(j => j.status === 'in_progress').length,
    review: jobs.filter(j => j.status === 'review').length,
    done_today: jobs.filter(j => {
      if (j.status !== 'done') return false;
      const today = new Date().toISOString().split('T')[0];
      return j.completed_at?.startsWith(today);
    }).length,
    failed: jobs.filter(j => j.status === 'failed').length,
  };

  // Sort: review first (needs attention), then in_progress, queued, failed, done
  const sortedJobs = [...jobs].sort((a, b) => {
    const order: Record<string, number> = { review: 0, in_progress: 1, queued: 2, failed: 3, done: 4, rejected: 5, cancelled: 6 };
    const oa = order[a.status] ?? 7;
    const ob = order[b.status] ?? 7;
    if (oa !== ob) return oa - ob;
    if (a.status === 'queued' && b.status === 'queued') {
      if (a.priority !== b.priority) return b.priority - a.priority;
    }
    return new Date(b.created).getTime() - new Date(a.created).getTime();
  });

  const filters: { key: FilterType; label: string; count?: number }[] = [
    { key: 'all', label: 'Tous' },
    { key: 'review', label: 'À valider', count: stats.review },
    { key: 'queued', label: 'En attente', count: stats.queued },
    { key: 'in_progress', label: 'En cours', count: stats.in_progress },
    { key: 'done', label: 'Terminés' },
    { key: 'failed', label: 'Échoués', count: stats.failed },
  ];

  const hasMore = currentPage < totalPages;

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Chargement de la pipeline...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Pipeline</h1>
          <p className="text-gray-500 mt-1">File d'attente des jobs de production</p>
        </div>
        <button
          onClick={() => { setJobs([]); setCurrentPage(1); fetchJobs(1, false); }}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title="Rafraîchir"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="text-xs text-gray-400">{lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
        </button>
      </div>

      {/* Alert: jobs to review */}
      {stats.review > 0 && (
        <div className="mb-4 bg-purple-50 border-2 border-purple-300 rounded-xl p-4 flex items-center gap-3">
          <span className="text-2xl">🔍</span>
          <div>
            <p className="font-bold text-purple-800">{stats.review} job{stats.review > 1 ? 's' : ''} en attente de validation</p>
            <p className="text-sm text-purple-600">Cliquez sur un job pour le valider ou le rejeter</p>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-purple-700">{stats.review}</div>
          <div className="text-xs text-purple-600">À valider</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-yellow-700">{stats.queued}</div>
          <div className="text-xs text-yellow-600">En attente</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-blue-700">{stats.in_progress}</div>
          <div className="text-xs text-blue-600">En cours</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-green-700">{stats.done_today}</div>
          <div className="text-xs text-green-600">Terminés aujourd'hui</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <div className="text-2xl font-bold text-red-700">{stats.failed}</div>
          <div className="text-xs text-red-600">Échoués</div>
        </div>
      </div>

      {/* Filters + archive toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                filter === f.key
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              {f.label}
              {f.count !== undefined && f.count > 0 && (
                <span className="ml-1.5 bg-white/60 px-1.5 py-0.5 rounded-full text-xs">{f.count}</span>
              )}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={e => setIncludeArchived(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Inclure archivés (7j+)
        </label>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
          Erreur: {error}
        </div>
      )}

      {/* Counter: "X affichés / Y total" */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">
          {sortedJobs.length} affiché{sortedJobs.length > 1 ? 's' : ''} / {totalItems} total
        </p>
      </div>

      {/* Job list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {sortedJobs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-lg">Aucun job {filter !== 'all' ? `(${filters.find(f => f.key === filter)?.label})` : ''}</p>
            <p className="text-sm mt-1">Les jobs apparaîtront ici quand ils seront créés</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sortedJobs.map(job => {
              const typeConf = JOB_TYPE_CONFIG[job.type] || JOB_TYPE_CONFIG.other;
              const statusConf = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
              const clientName = job.business_name || job.expand?.submission_id?.business_name || job.submission_id;

              return (
                <div
                  key={job.id}
                  className={`px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                    job.status === 'in_progress' ? 'bg-blue-50/30' : ''
                  } ${job.status === 'review' ? 'bg-purple-50/30 border-l-4 border-l-purple-400' : ''}`}
                  onClick={() => setSelectedJob(job)}
                >
                  {/* Type icon */}
                  <div className="text-xl flex-shrink-0 w-8 text-center" title={typeConf.label}>
                    {typeConf.icon}
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">{clientName}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${statusConf.bg} ${statusConf.text} ${statusConf.color}`}>
                        {statusConf.badge} {STATUS_LABELS[job.status]}
                      </span>
                      {job.priority > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          job.priority >= 2 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {job.priority >= 2 ? '🔥 Urgente' : '⚡ Haute'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{typeConf.label}</span>
                      <span className="text-sm text-gray-600 truncate">{job.description || '—'}</span>
                    </div>
                    {job.error && (
                      <div className="mt-1 text-xs text-red-600 truncate" title={job.error}>
                        ❌ {job.error}
                      </div>
                    )}
                  </div>

                  {/* Duration/Time */}
                  <div className="text-right flex-shrink-0 w-24">
                    {job.status === 'in_progress' && job.started_at && (
                      <div className="text-sm text-blue-600 font-mono">⏱ {duration(job.started_at)}</div>
                    )}
                    {job.status === 'done' && job.started_at && job.completed_at && (
                      <div className="text-sm text-green-600 font-mono">{duration(job.started_at, job.completed_at)}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-0.5">{timeAgo(job.created)}</div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex gap-1" onClick={e => e.stopPropagation()}>
                    {job.status === 'review' && (
                      <>
                        <button
                          onClick={() => handleAction(job.id, 'validate')}
                          className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50 transition-colors font-medium"
                        >
                          ✅ Valider
                        </button>
                        <button
                          onClick={() => setRejectingJob(job)}
                          className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                        >
                          ❌
                        </button>
                      </>
                    )}
                    {(job.status === 'queued' || job.status === 'in_progress') && (
                      <button
                        onClick={() => handleAction(job.id, 'cancel')}
                        className="text-xs text-gray-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      >
                        Annuler
                      </button>
                    )}
                    {job.status === 'failed' && (
                      <button
                        onClick={() => handleAction(job.id, 'retry')}
                        className="text-xs text-gray-400 hover:text-blue-600 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        Relancer
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* "Voir plus" button */}
        {hasMore && (
          <div className="border-t border-gray-100 px-5 py-4 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  Voir plus ({totalItems - jobs.length} restants)
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Job Detail Modal (T13) */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onValidate={() => handleAction(selectedJob.id, 'validate')}
          onReject={() => {
            setRejectingJob(selectedJob);
            setSelectedJob(null);
          }}
        />
      )}

      {/* Rejection Modal (T12) */}
      {rejectingJob && (
        <RejectModal
          onCancel={() => setRejectingJob(null)}
          onConfirm={(reason) => handleAction(rejectingJob.id, 'reject', reason)}
        />
      )}
    </div>
  );
}
